import { z } from 'zod';

import { publishPetitionSchema } from '#src/app/shared/api/petitionDraft';

// =============================================================================
// Input Schema — the shape of the data as it's collected from the UI
// =============================================================================

type DraftInput = z.infer<typeof publishPetitionSchema>;

// =============================================================================
// Output Schema — the shape expected by the API mutation
// =============================================================================

const outputSchema = z.object({
	ask: z.string().min(1),
	description: z.string().min(1),
	aiAsk: z.string().nullable(),
	aiDescription: z.string().nullable(),
	geoReach: z.enum(['global', 'national', 'local']).nullable(),
	location: z.object({ localizedName: z.string().min(1), googlePlacesId: z.string().min(1) }).nullable(),
	media: z
		.object({ photo: z.object({ srcUrl: z.string().min(1), type: z.enum(['USER_UPLOADED', 'AI_GENERATED']) }) })
		.nullable(),
});

// =============================================================================
// Transform — converts the UI draft shape into the API mutation shape
// =============================================================================

type Location = DraftInput['petitionDraft']['location'];
const transformLocation = (location: Location) => {
	return location
		? {
				localizedName: location.name,
				googlePlacesId: location.id,
			}
		: null;
};

type Image = DraftInput['petitionDraft']['image'];
const transformImage = (image: Image) => {
	return image
		? {
				photo: {
					srcUrl: image.url,
					type: image.type,
				},
			}
		: null;
};

type PetitionDraft = DraftInput['petitionDraft'];
const transformPetitionDraftFields = (petitionDraft: PetitionDraft) => ({
	location: transformLocation(petitionDraft.location),
	media: transformImage(petitionDraft.image),
});

const transformDraft = (draft: DraftInput) => {
	const { recommendedDraft, petitionDraft } = draft;
	return {
		ask: petitionDraft.ask,
		description: petitionDraft.description,
		aiAsk: recommendedDraft?.ask ?? null,
		aiDescription: recommendedDraft?.description ?? null,
		geoReach: petitionDraft.reach ?? null,
		...transformPetitionDraftFields(petitionDraft),
	};
};

export const schema = publishPetitionSchema.transform(transformDraft).pipe(outputSchema);
export type Schema = z.infer<typeof schema>;
